import MiniSearch from 'minisearch';
import { bindSearchInput, debounce } from './search-input';
import {
  applyCollapsedGroupHits,
  highlightCardText,
  normalizeQuery,
} from './search-highlight';
import { decodeCatalogProduct, renderProductCards } from './product-card-render';

const ANALYTICS_DEBOUNCE_MS = 300;
const COMMERCE_TEXT_SELECTORS = 'a.title, [data-testid="hs-card-group-test-link"]';
const SORT_POPULAR = 'popular';
const SORT_PRICE_ASC = 'price_asc';
const SORT_PRICE_DESC = 'price_desc';
const SORT_NAME_ASC = 'name_asc';
const DEFAULT_PAGE_LIMIT = 15;
const PAGINATION_WINDOW = 8;

function readConfig(root) {
  const pageLimit = parseInt(root.getAttribute('data-hs-catalog-page-limit') || '', 10);

  return {
    indexUrl: root.getAttribute('data-hs-catalog-index') || '',
    language: root.getAttribute('data-hs-catalog-language') || '',
    storeId: root.getAttribute('data-hs-catalog-store-id') || '1',
    fallbackRoute: root.getAttribute('data-hs-catalog-fallback-route') || '',
    pageLimit: Number.isFinite(pageLimit) && pageLimit > 0 ? pageLimit : DEFAULT_PAGE_LIMIT,
    paginationText: root.getAttribute('data-hs-pagination-text') || 'Showing %d to %d of %d (%d Pages)',
    showAllLabel: root.getAttribute('data-hs-show-all-label') || 'Show all',
    hideLabel: root.getAttribute('data-hs-hide-label') || 'Hide',
    saleLabel: root.getAttribute('data-hs-sale-label') || 'SALE',
    newLabel: root.getAttribute('data-hs-new-label') || 'NEW',
    cartLabel: root.getAttribute('data-hs-cart-label') || '',
    inCartLabel: root.getAttribute('data-hs-in-cart-label') || '',
    loadingLabel: root.getAttribute('data-hs-loading-label') || '',
    trackingEntrypoint: root.getAttribute('data-tracking-entrypoint') || '',
  };
}

function readCurrencyCookie() {
  const match = String(document.cookie || '').match(/(?:^|; )currency=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function storageKey(storeId, language) {
  return 'hs-catalog-index:' + storeId + ':' + language + ':' + readCurrencyCookie();
}

function readCachedCatalog(config) {
  try {
    const raw = window.sessionStorage.getItem(storageKey(config.storeId, config.language));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.products)) {
      return null;
    }

    return parsed;
  } catch (err) {
    return null;
  }
}

function writeCachedCatalog(config, payload) {
  try {
    window.sessionStorage.setItem(storageKey(config.storeId, config.language), JSON.stringify(payload));
  } catch (err) {
    // Ignore quota / private-mode failures. Filtering still works from memory.
  }
}

function documentsFromPayload(payload) {
  return (payload.products || []).map(function mapDoc(product) {
    const decoded = decodeCatalogProduct(product);
    const groups = Array.isArray(decoded.biomarker_groups) ? decoded.biomarker_groups : [];
    const biomarkerNames = [];

    for (let i = 0; i < groups.length; i += 1) {
      const tests = Array.isArray(groups[i].tests) ? groups[i].tests : [];
      for (let j = 0; j < tests.length; j += 1) {
        if (tests[j].name) {
          biomarkerNames.push(tests[j].name);
        }
      }
    }

    return Object.assign({}, decoded, {
      id: decoded.id,
      biomarker_names: biomarkerNames.join(' '),
    });
  });
}

function numericValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function compareNames(left, right, language) {
  return String(left == null ? '' : left).localeCompare(
    String(right == null ? '' : right),
    language || undefined,
    { sensitivity: 'base', numeric: true }
  );
}

function compareBySort(left, right, sortKey, language) {
  if (sortKey === SORT_PRICE_ASC || sortKey === SORT_PRICE_DESC) {
    const direction = sortKey === SORT_PRICE_ASC ? 1 : -1;
    const priceDelta = direction * (numericValue(left.price_value) - numericValue(right.price_value));
    if (priceDelta !== 0) {
      return priceDelta;
    }
  } else if (sortKey !== SORT_NAME_ASC) {
    const orderDelta = numericValue(left.sort_order) - numericValue(right.sort_order);
    if (orderDelta !== 0) {
      return orderDelta;
    }
  }

  const nameDelta = compareNames(left.name, right.name, language);
  if (nameDelta !== 0) {
    return nameDelta;
  }

  return numericValue(left.id) - numericValue(right.id);
}

function sortedDocuments(documents, sortKey, language) {
  return (documents || []).slice().sort(function sortDocuments(left, right) {
    return compareBySort(left, right, sortKey, language);
  });
}

function escapeAttr(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function parsePositiveInt(value, fallback) {
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }

  return parsed;
}

function readPageFromUrl() {
  try {
    return parsePositiveInt(new URL(window.location.href).searchParams.get('page'), 1);
  } catch (err) {
    return 1;
  }
}

function paginateDocuments(documents, page, limit) {
  const total = (documents || []).length;
  const pages = Math.max(1, Math.ceil(total / limit) || 1);
  const current = Math.min(Math.max(1, page), pages);
  const startIndex = (current - 1) * limit;

  return {
    page: current,
    pages: pages,
    total: total,
    start: total ? startIndex + 1 : 0,
    end: Math.min(startIndex + limit, total),
    items: (documents || []).slice(startIndex, startIndex + limit),
  };
}

function formatPaginationSummary(template, from, to, total, pages) {
  const values = [from, to, total, pages];
  let index = 0;

  return String(template || '').replace(/%d/g, function replaceToken() {
    const value = values[index];
    index += 1;
    return String(value == null ? '' : value);
  });
}

function paginationWindow(page, pages) {
  if (pages <= PAGINATION_WINDOW) {
    return { start: 1, end: pages };
  }

  let start = page - Math.floor(PAGINATION_WINDOW / 2);
  let end = page + Math.floor(PAGINATION_WINDOW / 2);

  if (start < 1) {
    end += Math.abs(start) + 1;
    start = 1;
  }

  if (end > pages) {
    start -= end - pages;
    end = pages;
  }

  if (start < 1) {
    start = 1;
  }

  return { start: start, end: end };
}

function renderPaginationList(paged, hrefForPage) {
  if (paged.pages <= 1 || paged.total < 1) {
    return '';
  }

  const page = paged.page;
  const pages = paged.pages;
  const windowRange = paginationWindow(page, pages);
  let html = '<ul class="pagination">';

  if (page > 1) {
    html +=
      '<li><a href="' + escapeAttr(hrefForPage(1)) + '" data-hs-page="1">|&lt;</a></li>' +
      '<li><a href="' + escapeAttr(hrefForPage(page - 1)) + '" data-hs-page="' + (page - 1) + '">&lt;</a></li>';
  }

  for (let i = windowRange.start; i <= windowRange.end; i += 1) {
    if (i === page) {
      html += '<li class="active"><span>' + i + '</span></li>';
    } else {
      html +=
        '<li><a href="' + escapeAttr(hrefForPage(i)) + '" data-hs-page="' + i + '">' + i + '</a></li>';
    }
  }

  if (page < pages) {
    html +=
      '<li><a href="' + escapeAttr(hrefForPage(page + 1)) + '" data-hs-page="' + (page + 1) + '">&gt;</a></li>' +
      '<li><a href="' + escapeAttr(hrefForPage(pages)) + '" data-hs-page="' + pages + '">&gt;|</a></li>';
  }

  html += '</ul>';
  return html;
}

function createIndex(payload) {
  const miniSearch = new MiniSearch({
    fields: ['name', 'biomarker_names'],
    storeFields: [
      'id',
      'name',
      'href',
      'price',
      'special',
      'price_value',
      'sort_order',
      'tracking_unit_price',
      'biomarker_groups',
      'is_new',
    ],
    searchOptions: {
      prefix: true,
      fuzzy: 0.2,
      boost: {
        name: 3,
        biomarker_names: 1.5,
      },
    },
  });

  miniSearch.addAll(documentsFromPayload(payload));
  return miniSearch;
}

async function fetchCatalog(config) {
  const cached = readCachedCatalog(config);
  const response = await fetch(config.indexUrl, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    if (cached) {
      return cached;
    }

    throw new Error('catalog_index_failed');
  }

  const payload = await response.json();
  if (!payload || !Array.isArray(payload.products)) {
    if (cached) {
      return cached;
    }

    throw new Error('catalog_index_invalid');
  }

  if (
    !cached
    || cached.count !== payload.count
    || cached.updated_at !== payload.updated_at
    || cached.language !== payload.language
  ) {
    writeCachedCatalog(config, payload);
  }

  return payload;
}

function trackCatalogSearch(term) {
  if (window.HSTracking && typeof window.HSTracking.track === 'function') {
    window.HSTracking.track('shop:catalog_search', {
      search_term: String(term || '').toLowerCase(),
    });
  }
}

function replaceUrlParams(mutator) {
  if (!window.history || !window.history.replaceState) {
    return;
  }

  const url = new URL(window.location.href);
  mutator(url.searchParams);
  const query = url.searchParams.toString();
  const next = url.pathname + (query ? '?' + query : '') + url.hash;
  window.history.replaceState({}, '', next);
}

function catalogPageHref(fallbackRoute, query, page) {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  if (fallbackRoute) {
    params.set('route', fallbackRoute);
  }

  const trimmed = normalizeQuery(query);
  if (trimmed) {
    params.set('search', trimmed);
  } else {
    params.delete('search');
  }

  if (page > 1) {
    params.set('page', String(page));
  } else {
    params.delete('page');
  }

  const queryString = params.toString();
  return url.pathname + (queryString ? '?' + queryString : '') + url.hash;
}

function highlightResultCards(resultsRoot, query) {
  const cards = resultsRoot.querySelectorAll('.hs-commerce-card');
  for (let i = 0; i < cards.length; i += 1) {
    highlightCardText(cards[i], query, COMMERCE_TEXT_SELECTORS);
    applyCollapsedGroupHits(cards[i], query);
  }
}

function initProductLiveSearch(root) {
  const config = readConfig(root);
  const input = root.querySelector("input[name='search']");
  const browse = root.querySelector('[data-hs-product-browse]');
  const results = root.querySelector('[data-hs-product-results]');
  const empty = root.querySelector('[data-hs-product-empty]');
  const form = root.querySelector('[data-hs-search-form]');
  const sortSelect = root.querySelector('[data-hs-catalog-sort]');
  const paginationRoot = root.querySelector('[data-hs-product-pagination]');
  const paginationControls = root.querySelector('[data-hs-product-pagination-controls]');
  const paginationSummary = root.querySelector('[data-hs-product-pagination-summary]');

  if (!input || !browse || !results || !config.indexUrl) {
    return;
  }

  const browseHiddenAtLoad = browse.hidden;
  const emptyHiddenAtLoad = empty ? empty.hidden : true;
  const labels = {
    showAllLabel: config.showAllLabel,
    hideLabel: config.hideLabel,
    saleLabel: config.saleLabel,
    newLabel: config.newLabel,
    cartLabel: config.cartLabel,
    inCartLabel: config.inCartLabel,
    loadingLabel: config.loadingLabel,
    trackingEntrypoint: config.trackingEntrypoint,
  };

  let catalogPromise = null;
  let searchIndex = null;
  let allDocuments = null;
  let latestQuery = null;
  let latestSort = sortSelect ? sortSelect.value : SORT_POPULAR;
  let latestPage = readPageFromUrl();
  let latestSyncSearch = false;
  let latestScroll = false;
  let applying = false;

  function currentSort() {
    if (!sortSelect) {
      return SORT_POPULAR;
    }

    const value = sortSelect.value;
    if (
      value === SORT_PRICE_ASC
      || value === SORT_PRICE_DESC
      || value === SORT_NAME_ASC
    ) {
      return value;
    }

    return SORT_POPULAR;
  }

  function loadCatalog() {
    if (!catalogPromise) {
      catalogPromise = fetchCatalog(config).then(function onCatalog(payload) {
        searchIndex = createIndex(payload);
        allDocuments = documentsFromPayload(payload);
        return payload;
      }).catch(function onCatalogError() {
        catalogPromise = null;
        allDocuments = null;
        return null;
      });
    }

    return catalogPromise;
  }

  function hideLivePagination() {
    if (!paginationRoot) {
      return;
    }

    paginationRoot.hidden = true;
    if (paginationControls) {
      paginationControls.innerHTML = '';
    }
    if (paginationSummary) {
      paginationSummary.textContent = '';
    }
  }

  function showBrowse() {
    browse.hidden = browseHiddenAtLoad;
    results.hidden = true;
    results.innerHTML = '';
    hideLivePagination();
    if (empty) {
      empty.hidden = emptyHiddenAtLoad;
    }
  }

  function showResults(html, hasMatches) {
    browse.hidden = true;
    results.innerHTML = html;
    results.hidden = !hasMatches;
    if (empty) {
      empty.hidden = hasMatches;
    }
  }

  function renderLivePagination(paged, query) {
    if (!paginationRoot || !paginationControls || !paginationSummary) {
      return;
    }

    if (!paged.total || paged.pages <= 1) {
      hideLivePagination();
      return;
    }

    paginationControls.innerHTML = renderPaginationList(paged, function hrefForPage(page) {
      return catalogPageHref(config.fallbackRoute, query, page);
    });
    paginationSummary.textContent = formatPaginationSummary(
      config.paginationText,
      paged.start,
      paged.end,
      paged.total,
      paged.pages
    );
    paginationRoot.hidden = false;
  }

  const track = debounce(function onTrack(term) {
    trackCatalogSearch(term);
  }, ANALYTICS_DEBOUNCE_MS);

  async function applyQuery(rawQuery, options) {
    const query = normalizeQuery(rawQuery);
    const sortKey = currentSort();
    const requestedPage = options && options.page ? options.page : 1;
    const syncSearch = options && options.syncSearch;
    const scroll = options && options.scroll;
    const payload = await loadCatalog();

    if (!payload || !searchIndex || !allDocuments) {
      if (!query) {
        showBrowse();
      }
      return;
    }

    const unsorted = query ? searchIndex.search(query) : allDocuments;
    const hits = sortedDocuments(unsorted, sortKey, config.language);
    const paged = paginateDocuments(hits, requestedPage, config.pageLimit);
    const html = renderProductCards(paged.items, labels);
    showResults(html, hits.length > 0);
    renderLivePagination(paged, query);
    if (query && paged.items.length > 0) {
      highlightResultCards(results, query);
    }

    replaceUrlParams(function updateParams(params) {
      if (config.fallbackRoute) {
        params.set('route', config.fallbackRoute);
      }
      if (syncSearch) {
        if (query) {
          params.set('search', query);
        } else {
          params.delete('search');
        }
      }
      if (paged.page > 1) {
        params.set('page', String(paged.page));
      } else {
        params.delete('page');
      }
    });

    if (scroll) {
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    track(query);
  }

  function queueApply(options) {
    const resetPage = !options || options.resetPage !== false;
    const nextQuery = options && options.query !== undefined ? options.query : input.value;
    const nextSort = currentSort();
    const sameQuery = latestQuery !== null && normalizeQuery(nextQuery) === normalizeQuery(latestQuery);
    const sameSort = nextSort === latestSort;
    latestQuery = nextQuery;
    latestSort = nextSort;
    latestSyncSearch = !!(options && options.syncSearch);
    latestScroll = !!(options && options.scroll);
    if (resetPage && !(sameQuery && sameSort)) {
      latestPage = 1;
    } else if (options && options.page) {
      latestPage = options.page;
    }

    if (applying) {
      return;
    }

    applying = true;

    const drain = function drainQueue() {
      const queuedQuery = latestQuery;
      const queuedSort = latestSort;
      const queuedPage = latestPage;
      const queuedSyncSearch = latestSyncSearch;
      const queuedScroll = latestScroll;
      return applyQuery(queuedQuery, {
        page: queuedPage,
        syncSearch: queuedSyncSearch,
        scroll: queuedScroll,
      }).then(function afterApply() {
        if (
          queuedQuery !== latestQuery
          || queuedSort !== latestSort
          || queuedPage !== latestPage
          || queuedSyncSearch !== latestSyncSearch
        ) {
          return drain();
        }

        return null;
      });
    };

    drain().then(function doneApply() {
      applying = false;
    }, function doneApplyError() {
      applying = false;
    });
  }

  bindSearchInput(input, function onInput(value) {
    queueApply({ query: value, resetPage: true });
  });

  if (sortSelect) {
    sortSelect.addEventListener('change', function onSortChange() {
      queueApply({ query: input.value, resetPage: true });
    });
  }

  if (paginationRoot) {
    paginationRoot.addEventListener('click', function onPaginationClick(event) {
      const link = event.target && event.target.closest ? event.target.closest('a[data-hs-page]') : null;
      if (!link || !paginationRoot.contains(link)) {
        return;
      }

      event.preventDefault();
      queueApply({
        query: input.value,
        page: parsePositiveInt(link.getAttribute('data-hs-page'), 1),
        resetPage: false,
        scroll: true,
      });
    });
  }

  if (form) {
    form.addEventListener('submit', function onSubmit(event) {
      event.preventDefault();
      queueApply({ query: input.value, resetPage: true, syncSearch: true });
    });
  }

  const idle = window.requestIdleCallback || function fallbackIdle(cb) {
    return window.setTimeout(cb, 250);
  };
  idle(function prefetchCatalog() {
    loadCatalog().then(function afterPrefetch() {
      queueApply({ query: input.value, resetPage: false });
    });
  });
}

function initAll() {
  const roots = document.querySelectorAll('[data-hs-product-live-search]');
  for (let i = 0; i < roots.length; i += 1) {
    initProductLiveSearch(roots[i]);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
